import { useState } from "react";
import { SENTIMENT_META, SIGNAL_META } from "../../data/ybf/storyComparisons";
import { getPillar } from "../../data/ybf/pillars";
import { kpiRAG } from "../../data/ybf/partners";
import { fmtCr } from "../../data/ybf/utils";
import { Mic, MapPin, Calendar, Play, Sparkles, Star, Target, Handshake, FolderKanban } from "lucide-react";

export default function StoryComparisonCard({ comparison }) {
  const c = comparison;
  const pillar  = getPillar(c.project.pillar);
  const sentiment = SENTIMENT_META[c.sentiment];
  const signal  = SIGNAL_META[c.signal];
  const scoreRag = c.project.scoreRag;
  const kpi = kpiRAG(c.project.kpiCompletion);
  const [playing, setPlaying] = useState(false);

  const speak = () => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    if (playing) { window.speechSynthesis.cancel(); setPlaying(false); return; }
    const u = new SpeechSynthesisUtterance(c.excerpt);
    u.rate = 0.95;
    u.onend = () => setPlaying(false);
    setPlaying(true);
    window.speechSynthesis.speak(u);
  };

  return (
    <div
      data-testid={`story-comparison-${c.id}`}
      className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow"
    >
      {/* Header — beneficiary + signal */}
      <div
        className="px-4 py-3 border-b border-slate-100 flex items-start justify-between gap-3"
        style={{ background: `linear-gradient(90deg, ${pillar?.color}08, transparent 60%)` }}
      >
        <div className="flex items-start gap-3 min-w-0">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white text-xs flex-shrink-0"
            style={{ background: pillar?.color }}
          >
            {c.beneficiary.initials}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold text-slate-900 truncate">{c.beneficiary.name}</div>
            <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-500 truncate">
              <span className="inline-flex items-center gap-0.5"><MapPin size={9} /> {c.beneficiary.location}</span>
              <span>·</span>
              <span className="inline-flex items-center gap-0.5"><Calendar size={9} /> {c.beneficiary.capturedOn}</span>
            </div>
          </div>
        </div>

        <span
          data-testid={`story-comparison-signal-${c.id}`}
          className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${signal.chip} flex-shrink-0`}
          title={signal.label}
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: signal.fill }} />
          {signal.label}
        </span>
      </div>

      {/* Two columns: Voice AI sample · Project health */}
      <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-100">
        {/* LEFT — Voice AI sample */}
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200">
              <Mic size={10} /> Voice AI sample
            </span>
            <button
              type="button"
              onClick={speak}
              data-testid={`story-comparison-listen-${c.id}`}
              className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 hover:bg-violet-200 transition-colors"
            >
              <Play size={9} fill="currentColor" /> {playing ? "Stop" : "Listen"}
            </button>
          </div>

          {/* Sentiment chip — front and centre */}
          <div className="mb-2">
            <span
              data-testid={`story-comparison-sentiment-${c.id}`}
              className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2 py-0.5 rounded-full border ${sentiment.chip}`}
            >
              <span>{sentiment.emoji}</span>
              <span>{sentiment.label} sentiment</span>
            </span>
          </div>

          {/* Excerpt */}
          <p className="text-xs text-slate-700 italic leading-relaxed border-l-2 border-violet-300 pl-3">
            "{c.excerpt}"
          </p>
          <div className="mt-2 text-[10px] text-slate-500 inline-flex items-center gap-1">
            <Mic size={9} /> Captured by Voice AI agent on {c.beneficiary.capturedOn}
          </div>
        </div>

        {/* RIGHT — Project health */}
        <div className="p-4">
          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 border border-teal-200">
            <FolderKanban size={10} /> Project health
          </span>

          <div className="mt-2">
            <div className="flex items-center gap-1.5">
              <span className="font-mono text-[10px] font-semibold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded whitespace-nowrap">
                {c.project.id}
              </span>
              <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${pillar?.chipBg} ${pillar?.chipText}`}>
                {pillar?.short}
              </span>
            </div>
            <div className="text-[12px] font-bold text-slate-900 mt-1 leading-snug">
              {c.project.name}
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5 inline-flex items-center gap-1">
              <Handshake size={10} className="text-slate-400" /> {c.project.partner}
            </div>
          </div>

          {/* Score + KPI chips */}
          <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
            <span
              data-testid={`story-comparison-score-${c.id}`}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${scoreRag.bg} ${scoreRag.text} ${scoreRag.border}`}
              title={`Project Score · ${scoreRag.label}`}
            >
              <Star size={9} fill={scoreRag.dot} strokeWidth={0} />
              Score {c.project.score.toFixed(1)}/5
            </span>
            <span
              data-testid={`story-comparison-kpi-${c.id}`}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                kpi.tone === "green" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : kpi.tone === "amber" ? "bg-amber-50 text-amber-700 border-amber-200"
                : "bg-rose-50 text-rose-700 border-rose-200"
              }`}
              title={`Partner KPI completion · ${kpi.label}`}
            >
              <Target size={9} strokeWidth={2.5} />
              KPIs {c.project.kpiCompletion}%
            </span>
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                c.project.status === "Active"
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-slate-100 text-slate-600 border-slate-200"
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${c.project.status === "Active" ? "bg-emerald-500" : "bg-slate-400"}`} />
              {c.project.status}
            </span>
          </div>

          {/* Mini funding row */}
          <div className="grid grid-cols-2 gap-2 mt-3">
            <div className="bg-slate-50 rounded-lg p-1.5">
              <div className="text-[9px] uppercase tracking-wider text-slate-500 font-medium">Sanctioned</div>
              <div className="text-[12px] font-bold text-slate-900">{fmtCr(c.project.sanctionedCr)}</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-1.5">
              <div className="text-[9px] uppercase tracking-wider text-slate-500 font-medium">
                Utilised <span className="text-slate-400 normal-case font-normal">· {c.project.utilisePct}%</span>
              </div>
              <div className="text-[12px] font-bold text-slate-900">{fmtCr(c.project.utilisedCr)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Insight footer */}
      <div
        className="px-4 py-2.5 border-t border-slate-100 flex items-start gap-2"
        style={{ background: `${signal.fill}08` }}
      >
        <Sparkles size={12} className="mt-0.5 flex-shrink-0" style={{ color: signal.fill }} />
        <p className="text-[11px] text-slate-700 leading-snug">
          <span className="font-bold" style={{ color: signal.fill }}>
            {signal.label} ·{" "}
          </span>
          {c.insight}
        </p>
      </div>
    </div>
  );
}
